/**
 * Table view for group events
 * Shows a sortable table with event details
 */

import { useNavigate } from 'react-router-dom'
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Typography,
  Chip,
  Avatar,
  TablePagination,
} from '@mui/material'
import { Event as EventIcon, LocationOn as LocationIcon, People as PeopleIcon } from '@mui/icons-material'
import { CalendarEventDto, getEventStatusLabel, getEventStatusColor } from '@/api/types/calendarEvent'
import { format, parseISO } from 'date-fns'

type SortableField = 'startDateTime' | 'title' | 'location' | 'attendees' | 'status'
type SortDirection = 'asc' | 'desc'

interface EventsTableViewProps {
  events: CalendarEventDto[]
  sortBy: SortableField
  sortDirection: SortDirection
  onSortChange: (field: SortableField) => void
  page: number
  pageSize: number
  totalCount: number
  onPageChange: (newPage: number) => void
  onPageSizeChange: (newPageSize: number) => void
  /** Group ID for return navigation */
  groupId?: string
}

export function EventsTableView({
  events,
  sortBy,
  sortDirection,
  onSortChange,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  groupId,
}: EventsTableViewProps) {
  const navigate = useNavigate()

  const handleEventClick = (eventId: string) => {
    // Include return URL to navigate back to group page
    const returnUrl = groupId ? `/groups/${groupId}?tab=events` : undefined
    const url = returnUrl
      ? `/events/${eventId}?returnTo=${encodeURIComponent(returnUrl)}`
      : `/events/${eventId}`
    navigate(url)
  }

  const handleSort = (field: SortableField) => {
    onSortChange(field)
  }

  const createSortHandler = (field: SortableField) => () => {
    handleSort(field)
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <TableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'startDateTime'}
                  direction={sortBy === 'startDateTime' ? sortDirection : 'asc'}
                  onClick={createSortHandler('startDateTime')}
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'title'}
                  direction={sortBy === 'title' ? sortDirection : 'asc'}
                  onClick={createSortHandler('title')}
                >
                  Title
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'location'}
                  direction={sortBy === 'location' ? sortDirection : 'asc'}
                  onClick={createSortHandler('location')}
                >
                  Location
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'attendees'}
                  direction={sortBy === 'attendees' ? sortDirection : 'asc'}
                  onClick={createSortHandler('attendees')}
                >
                  Attendees
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'status'}
                  direction={sortBy === 'status' ? sortDirection : 'asc'}
                  onClick={createSortHandler('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    No events found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              events.map((event) => (
                <TableRow
                  key={event.id}
                  hover
                  onClick={() => handleEventClick(event.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2" fontWeight={500}>
                        {format(parseISO(event.startDateTime), 'MMM d, yyyy')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {event.isAllDay
                          ? 'All day'
                          : format(parseISO(event.startDateTime), 'h:mm a')}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar
                        src={event.coverImageUrl}
                        variant="rounded"
                        sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}
                      >
                        <EventIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {event.title}
                        </Typography>
                        {event.quickDescription && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 1,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {event.quickDescription}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {event.location?.name ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          {event.location.name}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PeopleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2">
                        {event.attendanceStats?.attendingCount ?? 0}
                        {event.maxAttendees && ` / ${event.maxAttendees}`}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getEventStatusLabel(event.status)}
                      color={getEventStatusColor(event.status)}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={totalCount}
        rowsPerPage={pageSize}
        page={page}
        onPageChange={(_, newPage) => onPageChange(newPage)}
        onRowsPerPageChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
      />
    </Paper>
  )
}
